// Pure Template-mutation helpers used by the Template editor screen.
// Per spec/build-plan.md P-031 row + tech-arch.md §2.4. Co-located here
// so template-editor.tsx fits under the OC §3.1 ≤200-LOC target.
//
// **Each function returns a NEW Template object** when something changed,
// or the same reference (`return t`) when the operation was a no-op
// (unknown blockId, missing instanceId, out-of-bounds move). The store's
// `setState` no-op detection (`next === state` check, see store.ts D-082)
// then skips the listener fan-out, avoiding spurious re-renders.

import { newId } from '../../lib/id'
import { blocks, type BlockId } from '../../registry/blocks'
import type { Template, TemplateBlockInstance } from '../../store/schemas'

function nowIso(): string {
  return new Date().toISOString()
}

/**
 * Append a fresh Block instance to the Template's `blocks`. The `options`
 * field is seeded from the BlockDefinition's `defaultOptions`. Returns
 * the same Template reference if `blockId` is not registered.
 */
export function addBlockToTemplate(t: Template, blockId: string): Template {
  const def = blocks[blockId as BlockId]
  if (def === undefined) return t
  const instance: TemplateBlockInstance = {
    instanceId: newId(),
    blockId,
    options: def.defaultOptions,
  }
  return { ...t, blocks: [...t.blocks, instance], updatedAt: nowIso() }
}

/**
 * Drop the Block instance with the given `instanceId`. Returns the same
 * Template reference if no such instance exists.
 */
export function removeBlockFromTemplate(t: Template, instanceId: string): Template {
  if (!t.blocks.some((b) => b.instanceId === instanceId)) return t
  return {
    ...t,
    blocks: t.blocks.filter((b) => b.instanceId !== instanceId),
    updatedAt: nowIso(),
  }
}

/**
 * Swap the Block instance at `instanceId` with its neighbour in the given
 * direction (`-1` = up, `+1` = down). Returns the same Template reference
 * if the instance is missing or the move would go out of bounds.
 */
export function moveBlockInTemplate(
  t: Template,
  instanceId: string,
  delta: -1 | 1,
): Template {
  const idx = t.blocks.findIndex((b) => b.instanceId === instanceId)
  if (idx === -1) return t
  const target = idx + delta
  if (target < 0 || target >= t.blocks.length) return t
  const next = t.blocks.slice()
  const tmp = next[idx]!
  next[idx] = next[target]!
  next[target] = tmp
  return { ...t, blocks: next, updatedAt: nowIso() }
}

/**
 * Replace the `options` payload on the Block instance with `instanceId`.
 * Returns the same Template reference if no such instance exists.
 */
export function setBlockOptionsInTemplate(
  t: Template,
  instanceId: string,
  options: unknown,
): Template {
  if (!t.blocks.some((b) => b.instanceId === instanceId)) return t
  return {
    ...t,
    blocks: t.blocks.map((b) =>
      b.instanceId === instanceId ? { ...b, options } : b,
    ),
    updatedAt: nowIso(),
  }
}

/**
 * Set the Template's display `name`. Returns the same Template reference
 * if `name` is identical (the per-keystroke dispatch from the inline-edit
 * title would otherwise churn the store's listener queue when the user
 * types and immediately backspaces back to the same value).
 *
 * Empty `name` is allowed at the schema layer post-D-135; the editor
 * renders `name || 'Untitled template'` so empty persists silently and
 * the placeholder copy lives at the render seam, never in storage.
 */
export function setTemplateName(t: Template, name: string): Template {
  if (t.name === name) return t
  return { ...t, name, updatedAt: nowIso() }
}

/**
 * Replace the `defaultInputs` payload on the Block instance with
 * `instanceId` (P-037.31 / D-139). Mirrors `setBlockOptionsInTemplate`'s
 * shape: returns the same Template reference if no instance matches
 * `instanceId` (the D-082 + D-093 no-op short-circuit pattern; the
 * store's `setState` no-op detection then skips the listener fan-out).
 *
 * Used by the Template editor's Defaults section (currently scoped to
 * the 3 whitelisted Blocks per D-139: `section-heading`,
 * `sub-section-heading`, `text`). The Defaults form dispatches every
 * keystroke as a full `defaultInputs` object; downstream consumers
 * (`createEmailFromTemplate`) spread it over `BlockDefinition.defaultInputs`
 * at Email creation time.
 */
export function setBlockDefaultInputsInTemplate(
  t: Template,
  instanceId: string,
  defaultInputs: unknown,
): Template {
  if (!t.blocks.some((b) => b.instanceId === instanceId)) return t
  return {
    ...t,
    blocks: t.blocks.map((b) =>
      b.instanceId === instanceId ? { ...b, defaultInputs } : b,
    ),
    updatedAt: nowIso(),
  }
}
